/**
 * 通用连接点组件
 *
 * 基于 React Flow Handle 的封装，提供统一的连接点样式
 */

import React from 'react';
import { Handle, Position, type HandleProps } from '@xyflow/react';
import { cn } from './nodeStyles';

export interface CommonHandleProps extends Omit<HandleProps, 'position'> {
  /** 位置：left 为输入，right 为输出 */
  position: 'left' | 'right' | 'top' | 'bottom';
  /** 是否显示标签 */
  label?: string;
  /** 是否高亮（执行中） */
  isHighlighted?: boolean;
  /** 是否已完成 */
  isCompleted?: boolean;
  /** 是否失败 */
  isFailed?: boolean;
}

const positionMap = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

export const CommonHandle: React.FC<CommonHandleProps> = React.memo(({
  position,
  label,
  isHighlighted = false,
  isCompleted = false,
  isFailed = false,
  className,
  ...props
}) => {
  // 连接点基础样式
  const baseClasses = 'w-3 h-3 rounded-full border-2 transition-all duration-150 cursor-pointer';

  // 状态样式 - 使用统一的颜色方案
  const stateClasses = cn(
    // 默认状态
    !isHighlighted && !isCompleted && !isFailed && 'bg-white border-gray-300 hover:border-paper-400 hover:bg-paper-50',
    // 高亮状态（执行中）
    isHighlighted && !isCompleted && !isFailed && 'bg-ink-800 border-paper-500 shadow-lg shadow-paper-200 animate-pulse',
    // 完成状态
    isCompleted && !isFailed && 'bg-success-500 border-success-500',
    // 失败状态
    isFailed && 'bg-error-500 border-error-500',
  );

  // 从 props 中提取 type，避免重复传递
  const { type, ...restProps } = props;

  return (
    <Handle
      type={type}
      position={positionMap[position]}
      className={cn(baseClasses, stateClasses, className)}
      {...restProps}
    >
      {label && (
        <span className="absolute text-xs text-gray-500 whitespace-nowrap pointer-events-none"
          style={{
            [position === 'left' ? 'left' : 'right']: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            margin: position === 'left' ? '0 0 0 8px' : '0 8px 0 0',
          }}
        >
          {label}
        </span>
      )}
    </Handle>
  );
});

CommonHandle.displayName = 'CommonHandle';

/** 输入连接点（左侧） */
export const InputHandle: React.FC<Omit<CommonHandleProps, 'type' | 'position'>> = (props) => (
  <CommonHandle type="target" position="left" {...props} />
);

/** 输出连接点（右侧） */
export const OutputHandle: React.FC<Omit<CommonHandleProps, 'type' | 'position'>> = (props) => (
  <CommonHandle type="source" position="right" {...props} />
);

export default CommonHandle;